import { Box, Stack } from "@mui/material";

import DetailedViewPageHeader from "@/App/Layouts/PageHeader/DetailedViewPageHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useOnboardingContext } from "@/common/contexts/onboarding";
import { RoutingHelper } from "@/common/helpers/routing";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { ApiEnumName } from "@/common/services/enum";
import { ViewVariant } from "@/common/ts/layout";
import { appCommonConfig } from "@/config/config";
import { EntityType, OnboardingDto } from "@/core/api/generated/v1";

import AppButton from "../../../Button/AppButton";
import AppIconButton from "../../../Button/AppIconButton";
import InlineApiEnumValue from "../../../Enum/InlineApiEnumValue";
import AppIcon from "../../../Icons/AppIcon";
import { AppModalCloseIconButton } from "../../../Modals/AppModalCloseIconButton";
import AppTypography from "../../../Text/AppTypography";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import { OnboardingStepChecklist } from "../Step/OnboardingStepChecklist";
import { commonViewContainerSx } from "./OnboardingView";

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};

interface Props extends BaseEntityViewInheritableProps<OnboardingDto> {
  displayProps?: Partial<typeof defaultDisplayProps>;
}

export function OnboardingStepsView({ displayProps, onClose }: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const onboardingContext = useOnboardingContext();

  return (
    <BaseEntityView
      entityType={EntityType.Onboarding}
      entityId={onboardingContext.onboarding?.id}
      entity={onboardingContext.onboarding}
      entityRequest={undefined}
      isIniting={onboardingContext.isIniting || !onboardingContext.onboarding}
    >
      {onboardingContext.onboarding && (
        <ViewLayout
          sx={{
            ...commonViewContainerSx,
          }}
          displayProps={{
            viewVariant: ViewVariant.Page,
          }}
          header={
            displayProps?.header && (
              <>
                <DetailedViewPageHeader
                  image={undefined}
                  title={
                    <>
                      {onboardingContext.onboarding?.definition?.title || (
                        <InlineApiEnumValue
                          type={ApiEnumName.OnboardingBuiltInType}
                          value={onboardingContext.onboarding?.definition?.builtInType}
                        />
                      )}
                    </>
                  }
                  subtitle={
                    <AppTypography variant='body1' color='textPrimary'>
                      {onboardingContext.onboarding?.definition?.subtitle || (
                        <>Step-by-step guide on using the {appCommonConfig.appName} application</>
                      )}
                    </AppTypography>
                  }
                  startActions={
                    displayProps?.actions && (
                      <AppIconButton
                        variant='text'
                        size='medium'
                        color='text'
                        tooltipProps={{ title: "Back to intro", isDefaultEnterDelays: true }}
                        onClick={() => {
                          history.push(ROUTE_PATH.ONBOARDING_VIEW_INTRO());
                        }}
                      >
                        <AppIcon of='goBack' />
                      </AppIconButton>
                    )
                  }
                  primaryActions={
                    displayProps?.actions &&
                    onboardingContext.onboarding.definition?.isSkippable && (
                      <Box>
                        <AppButton
                          variant='text'
                          size='medium'
                          color='text'
                          onClick={async () => {
                            await onboardingContext.skip();
                            history.push(RoutingHelper.getCurrentAppAreaRootPathOrDefault());
                          }}
                        >
                          Skip
                        </AppButton>
                      </Box>
                    )
                  }
                  closeAction={
                    displayProps.closeInHeader && onClose ? (
                      <AppModalCloseIconButton onClick={onClose} />
                    ) : undefined
                  }
                />
              </>
            )
          }
        >
          <ViewContentBlock
            sx={{
              pl: 6,
              pr: 6,
              borderRadius: (th) => th.shapeCustom.borderRadiusCardLarge,
            }}
          >
            <Box sx={{}}>
              <OnboardingStepChecklist />

              <Stack direction='row' sx={{ mt: 2 }}>
                {onboardingContext.currentStepPair && (
                  <AppButton
                    sx={{ ml: "auto" }}
                    variant='contained'
                    size='medium'
                    color='primary'
                    endIcon={<AppIcon of='arrowForward' />}
                    onClick={() => {
                      history.push(
                        ROUTE_PATH.ONBOARDING_VIEW_STEP({
                          stepDefinitionId: onboardingContext.currentStepPair?.definition.id || "",
                        }),
                      );
                    }}
                  >
                    Next
                  </AppButton>
                )}
                {!onboardingContext.currentStepPair && (
                  <AppButton
                    sx={{ ml: "auto" }}
                    variant='contained'
                    size='medium'
                    color='primary'
                    endIcon={<AppIcon of='arrowForward' />}
                    onClick={async () => {
                      await onboardingContext.complete();
                      history.push(ROUTE_PATH.ONBOARDING_VIEW_COMPLETED());
                    }}
                  >
                    Finish
                  </AppButton>
                )}
              </Stack>
            </Box>
          </ViewContentBlock>
        </ViewLayout>
      )}
    </BaseEntityView>
  );
}
